import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "@/lib/auth";
import { roleLabel } from "@/lib/cc";
import {
  LayoutDashboard, Briefcase, Users, ShieldCheck, FileCheck2,
  Globe, ClipboardCheck, BarChart3, Leaf, LogOut, Bell,
  Users2, History, Mail as MailIcon, MessageSquare, BookOpen, Trash2, FileSignature, UserCheck, UserMinus,
  Calendar, Star, GraduationCap, Webhook, Send
} from "lucide-react";
import { CandidateDrawerProvider } from "@/components/CandidateDrawer";

const NAV = [
  { to: "/app/pipeline", icon: LayoutDashboard, label: "Pipeline" },
  { to: "/app/jobs", icon: Briefcase, label: "Jobs" },
  { to: "/app/candidates", icon: Users, label: "Candidates" },
  { to: "/app/dashboard/interviews", icon: BarChart3, label: "Interview Dashboard" },
  { to: "/app/dashboard/compliance", icon: ShieldCheck, label: "CQC Compliance" },
  { to: "/app/dbs", icon: ShieldCheck, label: "DBS Tracking" },
  { to: "/app/references", icon: FileCheck2, label: "References" },
  { to: "/app/rtw", icon: Globe, label: "Right to Work" },
  { to: "/app/onboarding", icon: ClipboardCheck, label: "Onboarding" },
  { to: "/app/employees", icon: UserCheck, label: "Employees" },
  { to: "/app/reports", icon: BarChart3, label: "Reports" },
];

const ADMIN_NAV = [
  { to: "/app/admin/users", icon: Users2, label: "User Management", roles: ["super_admin", "hr_admin"] },
  { to: "/app/admin/policies", icon: BookOpen, label: "Policies", roles: ["super_admin", "hr_admin", "nominated_individual"] },
  { to: "/app/admin/interview-templates", icon: BookOpen, label: "Interview Templates", roles: ["super_admin", "hr_admin", "recruiter"] },
  { to: "/app/admin/offer-templates", icon: FileSignature, label: "Offer Letter Templates", roles: ["super_admin", "hr_admin", "recruiter"] },
  { to: "/app/admin/reference-requests", icon: FileCheck2, label: "Reference Requests", roles: ["super_admin", "hr_admin"] },
  { to: "/app/admin/audit", icon: History, label: "Audit Trail", roles: ["super_admin", "hr_admin"] },
  { to: "/app/admin/email-log", icon: MailIcon, label: "Email Log", roles: ["super_admin", "hr_admin"] },
  { to: "/app/admin/sms-log", icon: MessageSquare, label: "SMS Log", roles: ["super_admin", "hr_admin"] },
  { to: "/app/admin/bulk-sms", icon: Send, label: "Bulk SMS", roles: ["super_admin", "hr_admin"] },
  { to: "/app/admin/reminders", icon: Bell, label: "Interview Reminders", roles: ["super_admin", "hr_admin"] },
  { to: "/app/admin/wipe-sample-data", icon: Trash2, label: "Wipe Sample Data", roles: ["super_admin"] },
];

const PAYROLL_NAV = [
  { to: "/app/admin/payroll-run", icon: FileSignature, label: "Payroll Run", roles: ["super_admin", "hr_admin"] },
];

const PEOPLE_NAV = [
  { to: "/app/admin/leavers", icon: UserMinus, label: "Leavers Register", roles: ["super_admin", "hr_admin"] },
  { to: "/app/admin/appraisals", icon: Star, label: "Appraisals", roles: ["super_admin", "hr_admin"] },
  { to: "/app/admin/training", icon: GraduationCap, label: "Training Records", roles: ["super_admin", "hr_admin", "nominated_individual"] },
  { to: "/app/admin/integrations", icon: Webhook, label: "Integrations", roles: ["super_admin", "hr_admin"] },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const onLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <CandidateDrawerProvider>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Rabah Concept Recruitment — Staff Portal</title>
      </Helmet>
      <div className="min-h-screen flex" style={{ background: "var(--cc-bg)" }}>
        {/* Sidebar */}
        <aside className="w-64 shrink-0 border-r border-[var(--cc-border)] bg-[var(--cc-bg)] flex flex-col" data-testid="sidebar">
          <div className="p-5 border-b border-[var(--cc-border)]">
            <Link to="/app/pipeline" className="flex items-center gap-3" data-testid="sidebar-brand">
              <img src="/rabah-logo.png" alt="Rabah Concept" className="w-10 h-10 object-contain" />
              <div>
                <div className="font-display font-bold text-lg leading-none">Rabah Concept</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--cc-text-soft)] mt-1">Recruitment</div>
              </div>
            </Link>
          </div>
          <nav className="flex-1 p-3 space-y-0.5 cc-scroll overflow-y-auto">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <n.icon className="w-4 h-4" />
                <span>{n.label}</span>
              </NavLink>
            ))}
            {ADMIN_NAV.some((n) => n.roles.includes(user?.role)) && (
              <div className="pt-4 mt-2 border-t border-[var(--cc-border)]">
                <div className="label-eyebrow px-3 mb-1.5">Provisioning</div>
                {ADMIN_NAV.filter((n) => n.roles.includes(user?.role)).map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                    data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <n.icon className="w-4 h-4" />
                    <span>{n.label}</span>
                  </NavLink>
                ))}
              </div>
            )}
            {PAYROLL_NAV.some((n) => n.roles.includes(user?.role)) && (
              <div className="pt-4 mt-2 border-t border-[var(--cc-border)]">
                <div className="label-eyebrow px-3 mb-1.5">Payroll</div>
                {PAYROLL_NAV.filter((n) => n.roles.includes(user?.role)).map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                    data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <n.icon className="w-4 h-4" />
                    <span>{n.label}</span>
                  </NavLink>
                ))}
              </div>
            )}
            {PEOPLE_NAV.some((n) => n.roles.includes(user?.role)) && (
              <div className="pt-4 mt-2 border-t border-[var(--cc-border)]">
                <div className="label-eyebrow px-3 mb-1.5">People Ops</div>
                {PEOPLE_NAV.filter((n) => n.roles.includes(user?.role)).map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                    data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <n.icon className="w-4 h-4" />
                    <span>{n.label}</span>
                  </NavLink>
                ))}
              </div>
            )}
          </nav>
          <div className="p-4 border-t border-[var(--cc-border)]">
            <div className="flex items-center gap-3 mb-3" data-testid="sidebar-user">
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm"
                   style={{ background: "var(--cc-primary)" }}>
                {user?.name?.[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{user?.name}</div>
                <div className="text-[11px] text-[var(--cc-text-soft)]">{roleLabel(user?.role)}</div>
              </div>
            </div>
            <button onClick={onLogout} className="btn-ghost w-full justify-center" data-testid="logout-button">
              <LogOut className="w-4 h-4 inline mr-1.5" /> Sign out
            </button>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 flex flex-col min-w-0">
          <div className="cc-topbar px-8 py-4 flex items-center justify-between sticky top-0 z-10">
            <div className="label-eyebrow">Rabah Concept ATS · {roleLabel(user?.role)}</div>
            <div className="flex items-center gap-3">
              <button className="btn-ghost" data-testid="topbar-notifications">
                <Bell className="w-4 h-4" />
              </button>
              <Link to="/" className="btn-secondary" data-testid="open-candidate-portal-button">View Candidate Portal</Link>
            </div>
          </div>
          <div className="flex-1 p-8 cc-scroll overflow-y-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </CandidateDrawerProvider>
  );
}
